import React, { useState, useEffect } from 'react';
import UploadDialog from './UploadDialog';
import { useNavigate, useLocation } from 'react-router-dom';
import UploadByFile from './UploadByFile';
import UploadByUrl from './UploadByUrl';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import EnhancedTable from './UploadTable';
import { search, syllabiExist } from '../../../functions/Api';
import Spinner from '../../Spinner';

function Upload({ viewAs }) {
  const [classes, setClasses] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [uploadMethod, setUploadMethod] = useState('');
  const [term, setTerm] = useState(null);
  const [updateErrors, setUpdateErrors] = useState([]);
  const [lookupError, setLookupError] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const classSearchParams = new URLSearchParams(window.location.search).getAll(
    'classKey'
  );

  // Parse class IDs from URL query string
  async function parseQueryString(qsClassKeys) {
    if (qsClassKeys.length > 0) {
      let searchTerms = { classKeys: [] };
      let invalidSearch = false;
      for (let i = 0; i < qsClassKeys.length && !invalidSearch; i++) {
        const classKey = qsClassKeys[i];
        if (classKey.match(/\d{4}-\d{5}/)) {
          const strm = classKey.substring(0, 4);
          const classNumber = classKey.substring(5);
          // searchTerms = { strm: strm, classNumber: classNumber };
          // searchTerms.push({ strm: strm, classNumber: classNumber });
          searchTerms.classKeys.push(classKey);
        } else {
          invalidSearch = true;
          setLookupError('Invalid search.');
        }
      }
      if (!invalidSearch) {
        try {
          let searchResults = await search(searchTerms, viewAs);
          searchResults = await syllabiExist(searchResults);
          setClasses(searchResults);
        } catch (e) {
          setLookupError('Error looking up classes.');
        }
      }
    }
  }

  useEffect(() => {
    if (classes === null && classSearchParams.length) {
      parseQueryString(classSearchParams);
    } else if (location.state && location.state.classes) {
      let selectedClasses = location.state.classes;
      setClasses(selectedClasses);
    } else if (classes === null) {
      // Shouldn't happen under normal use but set it to display a reasonable error message
      setClasses([]);
    }
  }, [location.state, classSearchParams]);

  useEffect(() => {
    if (isUpdate) {
      setIsUpdate(false);
    }
  }, [classes]);

  useEffect(() => {
    if (uploading) {
      setUploadResults([]);
      setUpdateErrors([]);
      setShowDialog(true);
    } else {
      setShowDialog(false);
    }
  }, [uploading]);

  const handleChange = (event) => {
    setUploadMethod(event.target.value);
  };

  const updateSyllabusStatus = (classNumber = '', strm = '') => {
    if (classNumber === '' && strm === '') {
      setClasses((prevState) => {
        setIsUpdate(true);
        return prevState.map((item) => {
          item.hasSyllabus = true;
          return item;
        });
      });
    } else {
      setClasses((prevState) => {
        setIsUpdate(true);
        return prevState.map((item) => {
          if (item.classNumber === classNumber && item.strm === strm) {
            item.hasSyllabus = false;
          }
          return item;
        });
      });
    }
  };

  // Navigation is now handled with the useNavigate hook
  if (lookupError) {
    return (
      <>
        {<h2>Upload Syllabus {term ? 'for ' + term : ''}</h2>}
        {
          <div className="alert alert-danger" role="alert">
            {lookupError}
          </div>
        }
      </>
    );
  } else if (!classes) {
    return <Spinner />;
  } else if (classes.length === 0) {
    return (
      <>
        {<h2>Upload Syllabus {term ? 'for ' + term : ''}</h2>}
        {
          <div className="alert alert-danger" role="alert">
            Classes not found.
          </div>
        }
      </>
    );
  } else {
    return (
      <>
        {<h2>Upload Syllabus {term ? 'for ' + term : ''}</h2>}

        {classes !== null && classes.length > 0 && (
          <div className="upload">
            {/** List of classes selected for uploading
			<ul>{classes.map(item => <li key={item.classNumber}>
				  {item.subject + " " + item.catalogNumber + " " + item.title + " (Class #" + item.classNumber + ")"} 
				</li>)}
			</ul>
			*/}

            {/** Show an alert */}
            {updateErrors.length > 0 && (
              <>
                {updateErrors.map((error, index) => (
                  <div className="alert alert-danger" role="alert" key={index}>
                    <div className="alert-icon">
                      <svg
                        title="Error"
                        className="svg-inline--fa fa-exclamation-triangle fa-w-18"
                        aria-labelledby="svg-inline--fa-title-fZn9kcUv3XVF"
                        data-prefix="fa"
                        data-icon="exclamation-triangle"
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 576 512"
                        data-fa-i2svg=""
                      >
                        <title id="svg-inline--fa-title-fZn9kcUv3XVF">
                          Error
                        </title>
                        <path
                          fill="currentColor"
                          d="M569.517 440.013C587.975 472.007 564.806 512 527.94 512H48.054c-36.937 0-59.999-40.055-41.577-71.987L246.423 23.985c18.467-32.009 64.72-31.951 83.154 0l239.94 416.028zM288 354c-25.405 0-46 20.595-46 46s20.595 46 46 46 46-20.595 46-46-20.595-46-46-46zm-43.673-165.346l7.418 136c.347 6.364 5.609 11.346 11.982 11.346h48.546c6.373 0 11.635-4.982 11.982-11.346l7.418-136c.375-6.874-5.098-12.654-11.982-12.654h-63.383c-6.884 0-12.356 5.78-11.981 12.654z"
                        ></path>
                      </svg>
                    </div>
                    <div className="alert-content">
                      We only accept PDF files at the moment. Please try again.
                    </div>
                    <div className="alert-close">
                      <button
                        aria-label="Close"
                        className="btn btn-circle btn-circle-alt-black close"
                        data-dismiss="alert"
                        data-ga="close cross"
                        data-ga-action="close"
                        data-ga-event="modal"
                        data-ga-name="onclick"
                        data-ga-region="main content"
                        data-ga-type="click"
                        type="button"
                        onClick={() => setUpdateErrors([])}
                      >
                        {/* <i class="fas fa-times" /> */}
                        <svg
                          className="svg-inline--fa fa-times fa-w-11"
                          aria-hidden="true"
                          focusable="false"
                          data-prefix="fas"
                          data-icon="times"
                          role="img"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 352 512"
                          data-fa-i2svg=""
                        >
                          <path
                            fill="currentColor"
                            d="M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.2 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.2 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z"
                          ></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
            {uploadResults.length > 0 && (
              <>
                {uploadResults.map((uploadResult, index) => (
                  <div
                    key={index}
                    className="alert alert-success alert-dismissable fade show"
                    role="alert"
                  >
                    <div className="alert-icon">
                      <svg
                        title="Success"
                        className="svg-inline--fa fa-check-circle fa-w-16"
                        aria-labelledby="svg-inline--fa-title-V4z4dpVdXhk6"
                        data-prefix="fa"
                        data-icon="check-circle"
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        data-fa-i2svg=""
                      >
                        <title id="svg-inline--fa-title-V4z4dpVdXhk6">
                          Success
                        </title>
                        <path
                          fill="currentColor"
                          d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628.001z"
                        ></path>
                      </svg>
                    </div>
                    <div className="alert-content">{uploadResult}</div>
                    <div className="alert-close">
                      <button
                        aria-label="Close"
                        className="btn btn-circle btn-circle-alt-black close"
                        // data-dismiss="alert"
                        // data-ga="close cross"
                        // data-ga-action="close"
                        // onClick = {()=> setUploadResults(uploadResults.splice(index,1))}
                        onClick={() => setUploadResults('')}
                        data-dismiss="alert"
                        data-ga-event="modal"
                        data-ga-name="onclick"
                        // data-ga-region="main content"
                        // data-ga-type="click"
                        type="button"
                      >
                        {/* <i class="fas fa-times" /> */}
                        <svg
                          className="svg-inline--fa fa-times fa-w-11"
                          aria-hidden="true"
                          focusable="false"
                          data-prefix="fas"
                          data-icon="times"
                          role="img"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 352 512"
                          data-fa-i2svg=""
                        >
                          <path
                            fill="currentColor"
                            d="M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.2 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.2 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z"
                          ></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {
              <div style={{ width: '100%' }}>
                <EnhancedTable
                  rows={classes}
                  updateSyllabusStatus={updateSyllabusStatus}
                  selected={[]}
                  setSelected={() => {}}
                  uploading={uploading}
                  setUploading={setUploading}
                  uploadResults={uploadResults}
                  setUploadResults={setUploadResults}
                  errors={updateErrors}
                  setErrors={setUpdateErrors}
                />
              </div>
            }

            <FormControl component="fieldset">
              <RadioGroup
                aria-label="uploadMethod"
                name="uploadMethod"
                value={uploadMethod}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="file"
                  control={<Radio />}
                  label="Upload a PDF"
                />

                {uploadMethod === 'file' && (
                  <UploadByFile
                    classes={classes}
                    updateSyllabusStatus={updateSyllabusStatus}
                    files={files}
                    setFiles={setFiles}
                    uploading={uploading}
                    setUploading={setUploading}
                    uploadResults={uploadResults}
                    setUploadResults={setUploadResults}
                    errors={updateErrors}
                    setErrors={setUpdateErrors}
                    showDialog={showDialog}
                    setShowDialog={setShowDialog}
                  />
                )}

                <FormControlLabel
                  value="url"
                  control={<Radio />}
                  label="Enter URL"
                />
                {uploadMethod === 'url' && (
                  <UploadByUrl
                    classes={classes}
                    updateSyllabusStatus={updateSyllabusStatus}
                    files={files}
                    setFiles={setFiles}
                    uploading={uploading}
                    setUploading={setUploading}
                    uploadResults={uploadResults}
                    setUploadResults={setUploadResults}
                    errors={updateErrors}
                    setErrors={setUpdateErrors}
                    showDialog={showDialog}
                    setShowDialog={setShowDialog}
                  />
                )}
              </RadioGroup>
            </FormControl>
          </div>
        )}

        {/** Dialog box for displaying upload status */}
        <UploadDialog
          open={showDialog}
          setOpen={setShowDialog}
          uploadResults={uploadResults}
          setUploadResults={setUploadResults}
          files={files}
          uploading={uploading}
          setUploading={setUploading}
          navigate={navigate}
        />
      </>
    );
  }
}

export default Upload;
